import clsx from 'clsx'
import React, { useEffect } from 'react'
import Select, {
  components,
  MenuProps,
  MultiValueGenericProps,
  OptionProps
} from 'react-select'

import Store from '@/components-latest/primitives/Icons/Store'

import Styles from './ComparisonSelector.module.scss'

interface OptionType {
  store_id: string
  restaurantname: string
  restaurant_name_i18n?: {
    en: string
    fr: string
    ar: string
  }
  availability?: number
  address?: string
  area: string
}

interface ComparisonSelectProps {
  options: OptionType[]
  value: OptionType[]
  onChange: (selected: OptionType[]) => void
  onCompare: () => void
  disabled?: boolean
}

const CustomOption = (props: OptionProps<OptionType, true>) => {
  const { data, isSelected, innerRef, innerProps, selectProps } = props
  const selectedCount = Array.isArray(selectProps.value)
    ? selectProps.value.length
    : 0
  const maxSelected = 2
  const shouldGreyOut = selectedCount >= maxSelected && !isSelected

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={clsx(Styles.option, {
        [Styles.selected]: isSelected,
        [Styles.greyedOut]: shouldGreyOut
      })}>
      <input
        type="checkbox"
        checked={isSelected}
        readOnly
        className={Styles.checkbox}
      />
      <label>{data.area}</label>
    </div>
  )
}

const MultiValue = (_: MultiValueGenericProps<OptionType>) => null // Hide chip rendering

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ValueContainer = ({ getValue, ...props }: any) => {
  const selected = getValue()
  let branchName = selected.length > 0 ? selected[0].area : 'All Branches'
  if (selected.length > 1){
    branchName = selected[0].area + ' | ' + selected[1].area
  }

  return (
    <components.ValueContainer {...props}>
      <div className={Styles.selectedDisplay}>{branchName}</div>
    </components.ValueContainer>
  );
};

const ComparisonSelect: React.FC<ComparisonSelectProps> = ({
  options,
  value,
  onChange,
  onCompare,
  disabled
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false)
  const handleChange = (selected: readonly OptionType[]) => {
    if (selected.length <= 2) {
      onChange([...selected])
    }
  }
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        selectRef.current &&
        !selectRef.current.controlRef.contains(event.target)
      ) {
        setMenuOpen(false)
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef = React.useRef<any>(null)
  const CustomMenu = (props: MenuProps<OptionType, true>) => {
    const selectedOptions = props.getValue()

    return (
      <components.Menu {...props}>
        {props.children}
        <div className={Styles.compareDropdown}>
          <button
            className={Styles.compareButton}
            onClick={e => {
              e.preventDefault()
              if (selectedOptions.length === 2) {
                onCompare()
                setMenuOpen(false)
                if (selectRef.current?.inputRef) {
                  selectRef.current.inputRef.blur()
                }
              }
            }}
            disabled={selectedOptions.length !== 2}
          >
            Compare
          </button>
        </div>
      </components.Menu>
    )
  }

  return (
    <div className={Styles.wrapper}>
      <Select
        ref={ref => {
          selectRef.current = ref
        }}
        options={options}
        value={value}
        onChange={handleChange}
        isMulti
        hideSelectedOptions={false}
        getOptionValue={option => option.store_id}
        classNames={{
          control: () => Styles.control,
          container: () => Styles.container,
          valueContainer: () => Styles.valueContainer,
          dropdownIndicator: () => Styles.dropdownIndicator
        }}
        closeMenuOnSelect={false}
        isClearable={false}
        placeholder="Select branches"
        components={{
          Option: CustomOption,
          ValueContainer,
          MultiValue,
          Menu: CustomMenu,
          IndicatorSeparator: () => null
        }}
        classNamePrefix="branch-select"
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
      />
    </div>
  )
}

export default ComparisonSelect
